/**
 * Subscription Module Styles — Stripe-inspired payment page
 */

import styled, { keyframes, css } from 'styled-components';

// --- Colors ---
const colors = {
  bg: '#f6f9fc',
  bgDark: '#0a2540',
  white: '#ffffff',
  border: '#e6e6e6',
  borderFocus: '#635bff',
  text: '#1a1a2e',
  textSecondary: '#6b7c93',
  textMuted: '#8898aa',
  primary: '#635bff',
  primaryHover: '#5147e5',
  success: '#3ecf8e',
  successBg: '#eafaf1',
  error: '#e25950',
  errorBg: '#fef2f2',
  cardBg: '#ffffff',
};

// --- Page Layout (Stripe-like split: left form, right summary) ---

export const PaymentPageContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  background: ${colors.white};
  overflow-y: auto;
`;

export const PaymentPageContent = styled.div<{ $isPhone?: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? 'column-reverse' : 'row')};
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  min-height: 100vh;
`;

// --- Left Panel (Payment Form) ---

export const PaymentFormPanel = styled.div<{ $isPhone?: boolean }>`
  flex: 1;
  padding: ${({ $isPhone }) => ($isPhone ? '24px 20px 40px' : '24px 20px 40px')};
  display: flex;
  flex-direction: column;
`;

// --- Right Panel (Order Summary) ---

export const OrderSummaryPanel = styled.div<{ $isPhone?: boolean }>`
  width: ${({ $isPhone }) => ($isPhone ? '100%' : '380px')};
  background: ${colors.bg};
  border-left: ${({ $isPhone }) => ($isPhone ? 'none' : `1px solid ${colors.border}`)};
  border-bottom: ${({ $isPhone }) => ($isPhone ? `1px solid ${colors.border}` : 'none')};
  padding: ${({ $isPhone }) => ($isPhone ? '24px 20px' : '48px 40px')};
  flex-shrink: 0;
`;

export const OrderSummaryTitle = styled.h3`
  font-weight: 600;
  font-size: 16px;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 24px 0;
`;

export const OrderSummaryAmount = styled.div`
  font-weight: 700;
  font-size: 36px;
  color: ${colors.text};
  margin-bottom: 4px;
`;

export const OrderSummaryCurrency = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: ${colors.textSecondary};
  margin-inline-start: 4px;
`;

export const OrderSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.border};
`;

export const OrderSummaryLabel = styled.span`
  font-weight: 400;
  font-size: 14px;
  color: ${colors.textSecondary};
`;

export const OrderSummaryValue = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${colors.text};
`;

export const OrderSummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0;
  margin-top: 4px;
`;

export const OrderSummaryTotalLabel = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${colors.text};
`;

export const OrderSummaryTotalValue = styled.span`
  font-weight: 700;
  font-size: 18px;
  color: ${colors.text};
`;

// --- Section Titles ---

export const PaymentSectionTitle = styled.h2`
  font-weight: 700;
  font-size: 20px;
  margin: 0 0 4px 0;
  color: ${colors.text};
`;

export const PaymentSectionSubtitle = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0 0 0 0;
`;

export const PaymentFormSectionLabel = styled.h3`
  font-weight: 600;
  font-size: 14px;
  color: ${colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 16px 0 16px 0;
`;

// --- Payment Method Selector ---

export const PaymentMethodsContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

export const PaymentMethodCard = styled.div<{ $isSelected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  padding: 14px 16px;
  border-radius: 8px;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? colors.primary : colors.border)};
  background: ${({ $isSelected }) => ($isSelected ? '#f8f7ff' : colors.white)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ $disabled, $isSelected }) =>
      $disabled ? colors.border : $isSelected ? colors.primary : '#c4c4c4'};
  }
`;

export const PaymentMethodRadio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.primary};
  cursor: pointer;
  flex-shrink: 0;
`;

export const PaymentMethodIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.bg};
  flex-shrink: 0;
`;

export const PaymentMethodInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const PaymentMethodTitle = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${colors.text};
`;

export const PaymentMethodDescription = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: ${colors.textMuted};
`;

// --- Payment Form Container (Moyasar overrides) ---

export const PaymentFormWrapper = styled.div`
  margin-top: 8px;

  .mysr-form {
    direction: ltr;
  }

  /* Override Moyasar form styles for Stripe-like look */
  .mysr-form .mysr-form-group label {
    font-weight: 500;
    font-size: 13px;
    color: ${colors.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
  }

  .mysr-form .mysr-form-group input {
    border: 1px solid ${colors.border};
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 15px;
    color: ${colors.text};
    background: ${colors.white};
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus {
      border-color: ${colors.primary};
      box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.12);
      outline: none;
    }
  }

  .mysr-form .mysr-form-group .mysr-card-form-group {
    border: 1px solid ${colors.border};
    border-radius: 6px;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: ${colors.primary};
      box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.12);
    }

    input {
      border: none;
      border-radius: 0;
      box-shadow: none;

      &:focus {
        box-shadow: none;
        border-color: transparent;
      }
    }
  }

  .mysr-form button[type="submit"],
  .mysr-form .mysr-form-button {
    background: ${colors.primary};
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-weight: 600;
    font-size: 15px;
    color: ${colors.white};
    cursor: pointer;
    transition: background 0.15s ease, transform 0.1s ease;
    width: 100%;
    margin-top: 8px;

    &:hover {
      background: ${colors.primaryHover};
    }

    &:active {
      transform: scale(0.99);
    }
  }

  .mysr-form .mysr-card-icon {
    opacity: 0.7;
  }

  .mysr-form .mysr-saved-badge {
    font-size: 12px;
    color: ${colors.textMuted};
  }
`;

export const PaymentDivider = styled.hr`
  border: none;
  border-top: 1px solid ${colors.border};
  margin: 24px 0;
`;

// --- Transaction Status Indicator ---

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
  max-width: 480px;
  margin: 0 auto;
`;

export const StatusSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid ${colors.border};
  border-top: 3px solid ${colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin-bottom: 28px;
`;

const checkmarkDraw = keyframes`
  to { stroke-dashoffset: 0; }
`;

export const StatusIconCircle = styled.div<{ $type: 'success' | 'fail' }>`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  background: ${({ $type }) => ($type === 'success' ? colors.successBg : colors.errorBg)};

  svg {
    width: 32px;
    height: 32px;
  }
`;

export const StatusTitle = styled.h3`
  font-weight: 700;
  font-size: 22px;
  color: ${colors.text};
  margin: 0 0 8px 0;
`;

export const StatusDescription = styled.p`
  font-weight: 400;
  font-size: 15px;
  color: ${colors.textSecondary};
  margin: 0 0 32px 0;
  max-width: 380px;
  line-height: 1.5;
`;

export const StatusButton = styled.button<{ $variant?: 'primary' | 'outlined' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 32px;
  min-width: 220px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 12px;

  ${({ $variant }) =>
    $variant === 'outlined'
      ? css`
    background: ${colors.white};
    color: ${colors.primary};
    border: 1.5px solid ${colors.border};
    &:hover { background: ${colors.bg}; border-color: #c4c4c4; }
  `
      : css`
    background: ${colors.primary};
    color: ${colors.white};
    border: none;
    &:hover { background: ${colors.primaryHover}; }
    &:active { transform: scale(0.99); }
  `}
`;

// --- Back Button ---

export const BackButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 32px;
  cursor: pointer;

  &:hover span {
    color: ${colors.primary};
  }
`;

export const BackButtonText = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.textSecondary};
  transition: color 0.15s ease;
`;

// --- Secure Badge ---

export const SecureBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 12px;
  color: ${colors.textMuted};

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
  }
`;

// --- Legacy exports (keep for PaymentCard compatibility) ---

export const PaymentCard = styled.div<{ $isPhone?: boolean }>`
  width: 100%;
`;
